import { useMemo } from 'react'
import {
  bindingConstraint,
  driverTotals,
  fcyLapEstimate,
  measuredFcyLapMs,
  stintDuration,
  stopBy,
  readClock,
  type Constraint,
} from '@racedash/shared'
import { Tile } from '../Tile'
import { useDriverStints } from '../useDriverStints'
import type { WidgetProps } from './props'

/**
 * When this car must stop for a limit, and why.
 *
 * A stint cap and a driver's remaining budget are both deadlines, and the
 * earliest one wins. Pit windows used to be a third deadline here, and the
 * tile wore their label; they now have a tile of their own, because "is the
 * window open" and "how long can this driver go on" are different questions
 * read by different people, and one tile answering both confused everyone.
 *
 * The number underneath is laps, not minutes, because a car can only pit as it
 * passes the pit entry. Counted from the last time it crossed the line, so a
 * lap already half run is half an opportunity closer.
 */
export default function StopBy({ car, data, now, context }: WidgetProps<'stopBy'>) {
  const regime = context.plan?.regime

  // Read the way the clock widget reads it, so "the race is stopped" means the
  // same thing on both tiles.
  const raw = data.session?.clock
  const clock = readClock(
    raw?.at
      ? { at: raw.at.toDate(), elapsedMs: raw.elapsedMs, remainingMs: raw.remainingMs, running: raw.running }
      : null,
    data.asAt(now),
  )

  // The same stints every other tile measures, against the session's clock.
  const { stints, at } = useDriverStints(car, data, context, now)

  const result = useMemo(() => {
    if (!car) return null

    const constraints: Constraint[] = []

    // --- the driver's stint ----------------------------------------------
    const current = stints.at(-1) ?? null

    if (current && regime?.stint?.maxDurationMs) {
      constraints.push({
        kind: 'stint',
        deadline: new Date(current.from.getTime() + regime.stint.maxDurationMs),
        label: 'stint limit',
      })
    }

    // --- what is left of this driver's day --------------------------------
    if (current?.driverId && regime?.driver?.maxTotalDrivingMs) {
      const { totals } = driverTotals(stints, at)
      const used = totals.find((t) => t.driverId === current.driverId)?.totalMs ?? 0
      // Their remaining budget, measured forward from now: the stint they are
      // on is already inside `used`, so the deadline is now plus whatever is
      // left rather than the start of the stint plus the cap.
      const left = regime.driver.maxTotalDrivingMs - used
      constraints.push({
        kind: 'driverBudget',
        deadline: new Date(now.getTime() + Math.max(0, left)),
        label: 'driver’s limit',
      })
    }

    // --- pace -------------------------------------------------------------
    const mine = data.laps.filter((l) => l.car === car)
    const lastCrossing = mine[0]?.completedAt?.toDate() ?? null
    const typical = current ? meanGreenLap(mine, current.from) : null
    const fcy = fcyLapEstimate({
      measuredMs: measuredFcyLapMs(data.laps),
      trackLengthMetres: context.trackLengthMetres ?? null,
      typicalLapMs: typical,
    })

    return {
      stint: current,
      answer: stopBy(bindingConstraint(constraints, now), {
        now,
        lastCrossing,
        typicalLapMs: typical,
        fcyLapMs: fcy,
        // A stopped clock during a session means the race is neutralised to a
        // standstill, and no laps are being completed.
        raceStopped: clock ? !clock.running : false,
      }),
    }
  }, [car, data, now, at, stints, regime, context, clock])

  if (!car) return <Tile label="Stop by">{blank('No car')}</Tile>
  if (!regime) return <Tile label="Stop by">{blank('No race plan')}</Tile>
  if (!result) return <Tile label="Stop by">{blank('—')}</Tile>

  const { answer, stint } = result
  const tone =
    answer.risk === 'critical' || answer.risk === 'missed'
      ? 'red'
      : answer.risk === 'tight'
        ? 'yellow'
        : undefined

  return (
    <Tile label="Stop by" tone={tone}>
      <div className="stopby">
        <div className="stopby-lap">
          {answer.laps == null ? '—' : answer.laps}
          <span className="stopby-unit">{answer.laps === 1 ? ' lap' : ' laps'}</span>
        </div>
        <div className="stopby-detail">{answer.detail}</div>
        <div className="stopby-foot">
          {stint && <span>Stint {fmtDuration(stintDuration(stint, at))}</span>}
        </div>
      </div>
    </Tile>
  )
}

function blank(reason: string) {
  return <div className="stopby-blank">{reason}</div>
}

/**
 * Mean of this stint's green laps.
 *
 * The stint's own pace, not the session's: a driver who has just got in, or a
 * track that has rubbered in, makes an average over the whole race the wrong
 * number to predict the next lap with.
 */
function meanGreenLap(
  laps: readonly {
    completedAt?: { toDate(): Date }
    lapTimeMs?: number | null
    inLap?: boolean
    outLap?: boolean
    s1Fcy?: boolean
    s2Fcy?: boolean
    s3Fcy?: boolean
    s1Sz?: boolean
    s2Sz?: boolean
    s3Sz?: boolean
  }[],
  since: Date,
): number | null {
  let sum = 0
  let n = 0
  for (const l of laps) {
    const at = l.completedAt?.toDate()
    if (!at || at < since) continue
    if (!l.lapTimeMs || l.lapTimeMs <= 0) continue
    if (l.inLap || l.outLap) continue
    if (l.s1Fcy || l.s2Fcy || l.s3Fcy || l.s1Sz || l.s2Sz || l.s3Sz) continue
    sum += l.lapTimeMs
    n++
  }
  return n > 0 ? Math.round(sum / n) : null
}

function fmtDuration(ms: number): string {
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  return h > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${m} min`
}

